import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { useState } from "react";
import { calculateDaysUntilDefault } from "~/lib/calculateLoanDefaults";
import { Button } from "~/components/ui/button";
import type { useLoanData } from "~/hooks/useLoanData";

const formatUSD = (value: string) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const formatCollateral = (value: string) => {
  return `${Number(value).toFixed(4)} gOHM`;
};

const formatAddress = (address: string) => {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const getEtherscanUrl = (address: string) => {
  return `https://etherscan.io/address/${address}`;
};

const columns: ColumnDef<
  NonNullable<ReturnType<typeof useLoanData>["loans"]>[number]
>[] = [
  {
    accessorKey: "borrower",
    header: "Wallet Address",
    cell: ({ row }) => (
      <a
        href={getEtherscanUrl(row.original.borrower.id)}
        target="_blank"
        rel="noopener noreferrer"
        className="font-mono"
      >
        {formatAddress(row.original.borrower.id)}
      </a>
    ),
  },
  {
    accessorKey: "cooler",
    header: "Cooler",
    cell: ({ row }) => (
      <a
        href={getEtherscanUrl(row.original.cooler)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-500 hover:text-blue-700"
      >
        View Cooler
      </a>
    ),
  },
  {
    accessorKey: "currentExpiryTimestamp",
    header: "Expiry Date",
    cell: ({ row }) => {
      const date = new Date(Number(row.original.currentExpiryTimestamp) * 1000);
      return (
        <div>
          {date.toLocaleDateString()} {date.toLocaleTimeString()}
        </div>
      );
    },
    sortingFn: (a, b) => {
      return a.original.currentExpiryTimestamp - b.original.currentExpiryTimestamp;
    },
  },
  {
    accessorKey: "daysUntilDefault",
    header: "Days Until Default",
    id: "daysUntilDefault",
    cell: ({ row }) => {

      const daysLeft = calculateDaysUntilDefault(Number(row.original.currentExpiryTimestamp));
      return (
        <div className={daysLeft <= 7 ? "text-red-500 font-bold" : ""}>
          {daysLeft}
        </div>
      );
    },
    sortingFn: (a, b) => {
      const aDays = calculateDaysUntilDefault(Number(a.original.currentExpiryTimestamp));
      const bDays = calculateDaysUntilDefault(Number( b.original.currentExpiryTimestamp));
      return aDays - bDays;
    },
  },
  {
    accessorKey: "principal",
    header: "Principal",
    cell: ({ row }) => formatUSD(row.original.principal),
  },
  {
    accessorKey: "interest",
    header: "Interest Due",
    cell: ({ row }) => formatUSD(row.original.interest),
  },
  {
    accessorKey: "collateral",
    header: "Collateral",
    cell: ({ row }) => formatCollateral(row.original.collateral),
  },
  {
    accessorKey: "loanId",
    header: "Loan ID",
  },
];

export function DataTable({
  data,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage
}: {
  data: NonNullable<ReturnType<typeof useLoanData>["loans"]>;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
}) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: "currentExpiryTimestamp", desc: false },
  ]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    enableSorting: true,
    pageCount: Math.ceil(data.length / 25),
    initialState: {
      pagination: {
        pageSize: 25,
      },
    },
  });

  return (
    <div className="bg-card rounded-md p-8 shadow">
      <div className="border rounded-md border-white/10">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={
                      header.column.getCanSort()
                        ? "cursor-pointer select-none"
                        : ""
                    }
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {{
                      asc: " 🔼",
                      desc: " 🔽",
                    }[header.column.getIsSorted() as string] ?? null}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="border-b border-white/10">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="p-4">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between py-4">
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
          <span className="flex items-center gap-1">
            <div>Page</div>
            <strong>
              {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </strong>
          </span>
        </div>
      </div>
    </div>
  );
}
